(() => {
  /* BEZELOR product page v4
     1. Phones/tablets: the section pills become a slim floating rail on the right, mid-screen, within thumb reach.
     2. FAQs: numbered, icon-led accordion with smooth open/close and a contact card.
     3. Review photos: the Judge.me photo viewer shows the whole photo instead of a cropped strip.
     4. Phones/tablets: the bottom Buy now bar stays hidden until the shopper swipes up (see bezelor-header-bar.css). */
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 989px)');

  /* ---------- 1. Section rail ---------- */
  const setupRail = () => {
    const nav = document.querySelector('.bezelor-pdp-flow [data-bezelor-tabs]');
    if (!nav || nav.dataset.bzRail) return;
    nav.dataset.bzRail = 'true';
    const links = [...nav.querySelectorAll('a[href^="#"]')];
    const panels = links.map((link) => document.querySelector(link.getAttribute('href'))).filter(Boolean);
    if (!links.length || !panels.length) return;
    const dot = document.createElement('span');
    dot.className = 'bz-rail__dot';
    dot.setAttribute('aria-hidden', 'true');
    nav.prepend(dot);

    let flashTimer;
    let idleTimer;
    const moveDot = (flash) => {
      const active = links.find((link) => link.classList.contains('is-active')) || links[0];
      dot.style.transform = 'translate(' + active.offsetLeft + 'px, ' + active.offsetTop + 'px)';
      if (!flash || !nav.classList.contains('is-on')) return;
      links.forEach((link) => link.classList.toggle('is-flash', link === active));
      clearTimeout(flashTimer);
      flashTimer = setTimeout(() => links.forEach((link) => link.classList.remove('is-flash')), 1600);
    };
    let lastActive = null;
    /* Tuck the rail away quickly so it does not cover what the shopper is reading.
       It peeks out briefly when it first appears and when a new section is reached. */
    const PEEK = 1300;
    const peek = () => {
      if (!nav.classList.contains('bz-rail')) return;
      nav.classList.remove('is-idle');
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => nav.classList.add('is-idle'), PEEK);
    };
    new MutationObserver(() => {
      const active = links.find((link) => link.classList.contains('is-active'));
      if (active === lastActive) return;
      const changed = lastActive !== null;
      lastActive = active;
      moveDot(true);
      if (changed && nav.classList.contains('is-on')) peek();
    }).observe(nav, { subtree: true, attributes: true, attributeFilter: ['class'] });

    const apply = () => {
      nav.classList.toggle('bz-rail', phone.matches);
      if (!phone.matches) nav.classList.remove('is-on', 'is-idle');
      requestAnimationFrame(() => moveDot(false));
    };
    apply();
    phone.addEventListener('change', apply);

    /* Show the rail only while the product information sections are on screen */
    let frame = null;
    let scrolled = false;
    const onScroll = (event) => {
      if (event && event.type === 'scroll') scrolled = true;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (!nav.classList.contains('bz-rail')) return;
        /* Only after the shopper has scrolled: while the page is still loading the gallery images have no
           height yet, so the sections briefly look as if they are on screen */
        if (!scrolled) return;
        const top = panels[0].getBoundingClientRect().top;
        const bottom = panels[panels.length - 1].getBoundingClientRect().bottom;
        const on = window.scrollY > window.innerHeight * 0.5 && top < window.innerHeight * 0.7 && bottom > window.innerHeight * 0.35;
        if (on && !nav.classList.contains('is-on')) {
          nav.classList.add('is-on');
          setTimeout(() => moveDot(true), 350);
          peek();
        } else if (!on) nav.classList.remove('is-on', 'is-idle');
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* Rail taps: scroll to the section ourselves (the bar's own handler offsets for a top bar) */
    const wake = () => {
      nav.classList.remove('is-idle');
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => nav.classList.add('is-idle'), 2600);
    };
    nav.addEventListener('click', (event) => {
      if (!nav.classList.contains('bz-rail')) return;
      /* Tucked away: the first tap only brings the rail back out */
      if (nav.classList.contains('is-idle')) {
        event.preventDefault();
        event.stopPropagation();
        wake();
        return;
      }
      wake();
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      links.forEach((item) => {
        item.classList.toggle('is-active', item === link);
        if (item === link) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      const top = target.getBoundingClientRect().top + window.scrollY - 84;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce.matches ? 'auto' : 'smooth' });
      if (history.replaceState) history.replaceState(null, '', link.getAttribute('href'));
    }, true);
    window.addEventListener('resize', () => moveDot(false), { passive: true });
  };

  /* ---------- 2. FAQs ---------- */
  const ICONS = [
    '<path d="M3 6h11v11H3zM14 10h4l3 3v4h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
    '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>',
    '<path d="M9 14 4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/>',
    '<circle cx="12" cy="12" r="8.5"/><circle cx="8.5" cy="10" r="1.3"/><circle cx="12" cy="7.6" r="1.3"/><circle cx="15.5" cy="10" r="1.3"/><path d="M12 20.5c-1.5 0-2-1.2-1.4-2.3.7-1.3 2.4-1.2 3.6-1.2"/>',
    '<rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3"/>'
  ];
  /* Open/close polish: the dark "open" look is a layer that fades in (a background swap between two gradients cannot be
     blended, which is what flashed), every colour change shares one short timing, and nothing moves differently from the answer. */
  const FAQ = '.bezelor-pdp-flow .bz-faqs.bz-faq-v4';
  const FAQ_CSS =
    'html body ' + FAQ + ' details{transition:border-color .2s ease,box-shadow .2s ease}' +
    'html body ' + FAQ + '.is-in details{transition:opacity .5s ease calc(var(--i,0)*.07s),translate .55s cubic-bezier(.2,.9,.25,1) calc(var(--i,0)*.07s),border-color .2s ease,box-shadow .2s ease}' +
    'html body ' + FAQ + ' details[open]:not(.is-closing){background:linear-gradient(180deg,#fff 0%,#fdfaf3 100%)!important}' +
    'html body ' + FAQ + ' details::after{content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none;opacity:0;background:radial-gradient(90% 120% at 100% 0%,rgba(212,180,107,.22),transparent 60%),#0e0d0b;transition:opacity .2s ease}' +
    'html body ' + FAQ + ' details[open]:not(.is-closing)::after{opacity:1}' +
    'html body ' + FAQ + ' details::before{z-index:2;transition:transform .25s ease}' +
    'html body ' + FAQ + ' details>summary,html body ' + FAQ + ' details>.bz-faq__a{position:relative;z-index:1}' +
    'html body ' + FAQ + ' .bz-faq__q{transition:color .2s ease}' +
    'html body ' + FAQ + ' .bz-faq__icon{transition:background .2s ease,color .2s ease,transform .25s cubic-bezier(.2,.9,.25,1)}' +
    'html body ' + FAQ + ' .bz-faq__toggle{transition:transform .25s cubic-bezier(.2,.9,.25,1),background .2s ease,border-color .2s ease}' +
    'html body ' + FAQ + ' .bz-faq__toggle::before,html body ' + FAQ + ' .bz-faq__toggle::after{transition:background .2s ease}' +
    'html body ' + FAQ + ' details.is-closing .bz-faq__a{color:#d9d0bf!important}' +
    'html body ' + FAQ + ' .bz-faq__a{will-change:height,opacity}';
  const setupFaqs = () => {
    if (!document.getElementById('bz-faq-smooth')) {
      const style = document.createElement('style');
      style.id = 'bz-faq-smooth';
      style.textContent = FAQ_CSS;
      document.head.appendChild(style);
    }
    document.querySelectorAll('.bezelor-pdp-flow .bz-faqs').forEach((list) => {
      if (list.dataset.bzFaq) return;
      list.dataset.bzFaq = 'true';
      list.classList.add('bz-faq-v4');
      const items = [...list.querySelectorAll(':scope > details')];
      items.forEach((item, index) => {
        const summary = item.querySelector('summary');
        const answer = item.querySelector(':scope > div');
        if (!summary || !answer) return;
        const text = summary.textContent.trim();
        summary.textContent = '';
        const num = document.createElement('span');
        num.className = 'bz-faq__num';
        num.textContent = String(index + 1).padStart(2, '0');
        const icon = document.createElement('span');
        icon.className = 'bz-faq__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<svg viewBox="0 0 24 24">' + (ICONS[index] || ICONS[1]) + '</svg>';
        const q = document.createElement('span');
        q.className = 'bz-faq__q';
        q.textContent = text;
        const toggle = document.createElement('span');
        toggle.className = 'bz-faq__toggle';
        toggle.setAttribute('aria-hidden', 'true');
        summary.append(num, icon, q, toggle);
        answer.classList.add('bz-faq__a');
        item.style.setProperty('--i', index);

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          if (item.dataset.animating) return;
          const opening = !item.open;
          if (opening) items.forEach((other) => { if (other !== item && other.open) close(other); });
          opening ? open(item) : close(item);
        });
      });
      const contact = document.createElement('div');
      contact.className = 'bz-faq-contact';
      contact.innerHTML =
        '<div class="bz-faq-contact__copy"><p class="bz-faq-contact__eyebrow">BEZELOR concierge</p>' +
        '<p class="bz-faq-contact__title">Still have a question?</p>' +
        '<p class="bz-faq-contact__text">Talk to a real person before or after you buy.</p></div>' +
        '<div class="bz-faq-contact__actions">' +
        '<a class="bz-faq-contact__btn" href="mailto:support@bezelor.com"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16v12H4z"/><path d="m4 7 8 6 8-6"/></svg>Email us</a>' +
        '<a class="bz-faq-contact__btn bz-faq-contact__btn--gold" href="https://wa.me/447950890542" target="_blank" rel="noopener"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 19l1.2-3.6A7.5 7.5 0 1 1 9 18.4z"/></svg>WhatsApp</a>' +
        '</div>';
      list.after(contact);
      if ('IntersectionObserver' in window && !reduce.matches) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            list.classList.add('is-in');
            contact.classList.add('is-in');
            io.disconnect();
          }
        }, { threshold: 0.15 });
        io.observe(list);
      } else {
        list.classList.add('is-in');
        contact.classList.add('is-in');
      }
    });
  };
  /* The answer box has bottom padding; it is animated with the height so the box really reaches zero (before, the padding
     stayed and the content below jumped at the start and end). Both directions: 220ms / 180ms, one easing. */
  const EASE = 'cubic-bezier(0.3, 0, 0.2, 1)';
  const finish = (item, done) => () => { delete item.dataset.animating; if (done) done(); };
  const open = (item) => {
    const answer = item.querySelector(':scope > div');
    item.open = true;
    if (reduce.matches || !answer.animate) return;
    item.dataset.animating = 'true';
    const cs = getComputedStyle(answer);
    const height = cs.height;
    const pb = cs.paddingBottom;
    answer.animate(
      [{ height: '0px', paddingBottom: '0px', opacity: 0 }, { height: height, paddingBottom: pb, opacity: 1 }],
      { duration: 220, easing: EASE }
    ).finished.then(finish(item), finish(item));
  };
  const close = (item) => {
    const answer = item.querySelector(':scope > div');
    if (reduce.matches || !answer.animate) {
      item.open = false;
      return;
    }
    item.dataset.animating = 'true';
    item.classList.add('is-closing');
    const cs = getComputedStyle(answer);
    const height = cs.height;
    const pb = cs.paddingBottom;
    const end = () => { item.open = false; item.classList.remove('is-closing'); };
    answer.animate(
      [{ height: height, paddingBottom: pb, opacity: 1 }, { height: '0px', paddingBottom: '0px', opacity: 0 }],
      { duration: 180, easing: EASE }
    ).finished.then(finish(item, end), finish(item, end));
  };

  /* ---------- 3. Review photo viewer ---------- */
  /* Judge.me builds its viewer when a photo is tapped. Large photos inside it are switched to
     "contain" so the whole photo shows; nothing else in the app is changed. */
  const fixViewer = () => {
    document.querySelectorAll('[class*="jm-"] img, [class*="jdgm"] img').forEach((img) => {
      if (img.dataset.bzLb) return;
      const rect = img.getBoundingClientRect();
      if (rect.width < window.innerWidth * 0.6 || rect.height < 160) return;
      let node = img.parentElement;
      let fixed = false;
      for (let i = 0; node && i < 12; i++, node = node.parentElement) {
        if (getComputedStyle(node).position === 'fixed') {
          fixed = true;
          break;
        }
      }
      if (!fixed) return;
      img.dataset.bzLb = 'true';
      img.classList.add('bz-jm-lb-img');
      img.parentElement.classList.add('bz-jm-lb-frame');
    });
  };
  let lbTimer;
  new MutationObserver(() => {
    clearTimeout(lbTimer);
    lbTimer = setTimeout(fixViewer, 150);
  }).observe(document.documentElement, { childList: true, subtree: true });

  ['click', 'touchend'].forEach((type) => {
    document.addEventListener(type, () => {
      setTimeout(fixViewer, 350);
      setTimeout(fixViewer, 1200);
    }, { capture: true, passive: true });
  });

  /* ---------- 4. Buy now bar: wait for the first swipe up ---------- */
  /* The CSS keeps the bar hidden until <html> gets the class bz-buybar-go. It is added by the first upward swipe
     (finger moves up 24px or more), by any scroll down past 60px (mouse wheel, keyboard, restored scroll position),
     and as a safety net after the page has been open for a long while (so the bar can never be lost). */
  const setupBuybar = () => {
    const root = document.documentElement;
    if (root.classList.contains('bz-buybar-go') || window.__bzBuybarArmed) return;
    window.__bzBuybarArmed = true;
    let startY = null;
    let done = false;
    const events = [['touchstart', onStart], ['touchmove', onMove], ['scroll', onScroll], ['wheel', onWheel]];
    function go() {
      if (done) return;
      done = true;
      root.classList.add('bz-buybar-go');
      events.forEach(([type, fn]) => window.removeEventListener(type, fn));
    }
    function onStart(event) {
      startY = event.touches && event.touches.length === 1 ? event.touches[0].clientY : null;
    }
    function onMove(event) {
      if (startY === null || !event.touches || !event.touches.length) return;
      if (startY - event.touches[0].clientY >= 24) go();
    }
    function onScroll() {
      if (window.scrollY > 60) go();
    }
    function onWheel(event) {
      if (event.deltaY > 10) go();
    }
    events.forEach(([type, fn]) => window.addEventListener(type, fn, { passive: true }));
    if (window.scrollY > 60) go();
    setTimeout(go, 45000);
  };

  const start = () => {
    setupRail();
    setupFaqs();
    setupBuybar();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
